import { Map as ImmutableMap, Record, type RecordOf } from 'immutable';

import { type Account, type AccountProps, defaultAccountProps } from '../accounts/accountsTypes';
import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from '../core/resourceStoreTypes';

export const GOALS_REDUCER_KEY = 'goalStore';

export type GoalTypes =
  | 'SAVE_FOR_EMERGENCY_FUND'
  | 'SAVE_FOR_OTHER'
  | 'SAVE_FOR_WEDDING'
  | 'SAVE_FOR_HOME'
  | 'SAVE_FOR_VACATION'
  | 'SAVE_FOR_CAR';

export const GoalTypeEnum = Object.freeze({
  SAVE_FOR_EMERGENCY_FUND: 'SAVE_FOR_EMERGENCY_FUND',
  SAVE_FOR_OTHER: 'SAVE_FOR_OTHER',
  SAVE_FOR_WEDDING: 'SAVE_FOR_WEDDING',
  SAVE_FOR_HOME: 'SAVE_FOR_HOME',
  SAVE_FOR_VACATION: 'SAVE_FOR_VACATION',
  SAVE_FOR_CAR: 'SAVE_FOR_CAR',
});

export type Goal = {
  type: GoalTypes;
  id: string;
  clientId?: string;
  accountId?: string;
  name: string;
  targetAmount: number;
  startOn: string;
  targetOn?: string | null;
  imageId: string;
  contributionAmount?: number;
  contributionFrequency?: string;
  contributedThisMonth?: number;
  tagId?: string;
  notes?: string;
  description?: string;
  savedSoFar?: number;
  spent?: number;
  completedOn?: string | null;
  initialAccount?: Account;
  isDeleted?: boolean;
  modifiedAt?: string;
  createdAt?: string;
  isTakenFromF2S?: boolean;
  txnIds?: string[];
};

export type GoalProps = Partial<Goal>;
export type GoalRecord = RecordOf<GoalProps>;

export type GoalsStoreProps = QcsSyncResourceStoreProps<GoalProps>;

export type GoalsStore = QcsSyncResourceStore<GoalProps, GoalsStoreProps>;

export const mkGoalsStore = Record<GoalsStoreProps>({
  resourcesById: ImmutableMap<string, GoalRecord>(),

  resourceName: 'GOALS',
  error: undefined,

  lastSyncDate: undefined,
  loadPending: true,

  isLoading: false,
});

// ////////////////////////////////////////////////////////////////////////////////////////////////
// Goal - Base Goal Properties
// ////////////////////////////////////////////////////////////////////////////////////////////////

// WARNING - If you provide any default goals props here, you need to explicitly remove them in
// mkUpdateGoal below, to prevent overwriting these values when someone is trying to update only
// specific fields in a goal.
//
const defaultGoalProps = {
  id: undefined,
  clientId: undefined,
  accountId: undefined,

  name: undefined,

  contributionAmount: undefined,
  contributionFrequency: undefined,
  contributedThisMonth: undefined,
  createdAt: undefined,

  tagId: undefined,
  targetAmount: undefined,
  targetOn: undefined,
  startOn: undefined,
  notes: undefined,
  imageId: undefined,
  description: undefined,
  completedOn: undefined,
  initialAccount: undefined,
  isDeleted: false,
  modifiedAt: undefined,
  savedSoFar: undefined,
  spent: undefined,

  isTakenFromF2S: undefined,

  txnIds: undefined,
};

// ================================================================================================
// Save for __________  Properties & Types
// ================================================================================================

export const mkSaveForOther = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_OTHER',
});
export const mkSaveForWedding = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_WEDDING',
});
export const mkSaveForCar = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_CAR',
});
export const mkSaveForHome = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_HOME',
});

export const mkSaveForEmergencyFund = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_EMERGENCY_FUND',
});

export const mkSaveForVacation = Record<GoalProps>({
  ...defaultGoalProps,
  type: 'SAVE_FOR_VACATION',
});

export const mkGoal = (type: GoalTypes, options: GoalProps): GoalRecord => {
  switch (type) {
    case 'SAVE_FOR_OTHER':
      return mkSaveForOther(options);
    case 'SAVE_FOR_EMERGENCY_FUND':
      return mkSaveForEmergencyFund(options);
    case 'SAVE_FOR_WEDDING':
      return mkSaveForWedding(options);
    case 'SAVE_FOR_CAR':
      return mkSaveForCar(options);
    case 'SAVE_FOR_HOME':
      return mkSaveForHome(options);
    case 'SAVE_FOR_VACATION':
      return mkSaveForVacation(options);
    default:
      //  log.warn('Was a new Goal Type added? Need to update mkGoal'); TODO: I do not know why this warning is here, it all seems to be working fine. Perhaps something else was to eventually be added? Regardless, custom goals work just fine so this warning isn't required.
      return mkSaveForOther(options);
  }
};

export const mkUpdateGoal = (type: GoalTypes, options: GoalProps): GoalRecord =>
  mkGoal(type, {
    ...options,
  });

export type GoalFundingAccountProps = Partial<AccountProps> & {
  availableBalance: number;
  goalsBalance: number;
  description?: string;
  goalContribution: number;
  subtext?: string;
};

const defaultGoalFundingAccountProps: GoalFundingAccountProps = {
  ...defaultAccountProps,
  availableBalance: 0,
  goalsBalance: 0,
  description: undefined,
  goalContribution: 0,
  subtext: undefined,
};

export type GoalFundingRecord = RecordOf<GoalFundingAccountProps>;

export const mkGoalFundingAccount = Record<GoalFundingAccountProps>({ ...defaultGoalFundingAccountProps });

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [GOALS_REDUCER_KEY]: GoalsStore;
  }
}
